import { type FC, useEffect, useMemo, useState } from "react";

import {
  noBarData,
  noData,
  validLineData,
  totalActivity,
  dailyActivity,
  lineChartTitles,
  isUserDeleted,
  type PerformanceWithAccountFilter,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
  normalizeFlowFilter,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { ChartWrapper, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { type ColumnOption } from "interfaces/columnOptions";
import { GenericPerformanceList } from "../../../Reporting/Content/shared/GenericPerformanceList";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import {
  sharedAccountReportingLineProps,
  sharedAccountReportingHorizontalBarProps,
} from "../../../Reporting/Content/shared";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type ConnectedProps, connect } from "react-redux";

import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { doLinkExport } from "features/Library/ExternalLinks/state/thunks/externalLinkCommandThunk";
import { bindActionCreators } from "@reduxjs/toolkit";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import styles from "./LinksReport.module.scss";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import * as performanceUtils from "../../Common/utils/performanceUtils";
import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { useLinkLineChartQuery, useLinkEngagementQuery, useLinkDetailsQuery } from "./Queries/queries";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type IconDefinition, faBullseyePointer, faEye } from "@fortawesome/pro-solid-svg-icons";
import { type LinkUserDetails } from "features/Reporting/types/content";

enum StatusIcons {
  Clicked = "Clicked",
  Viewed = "Viewed",
}

const StatusToIcon: { [key in StatusIcons]: IconDefinition } = {
  [StatusIcons.Clicked]: faBullseyePointer,
  [StatusIcons.Viewed]: faEye,
};

export type Props = PropsFromRedux & {
  accountId: number;
  linkName: string;
  linkId: number;
  filter: PerformanceWithAccountFilter;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
  flowId?: number;
};

const activityColorScale = [reportingColors.yellow, reportingColors.greenDark];
const activityLegendLabels = ["Impressions", "Clicks"];

export const LinksReport: FC<Props> = ({
  linkName,
  linkId,
  filter,
  accountId,
  setHasExportData,
  setExportAction,
  linkExport,
  setIsReportEnabled,
  resetIsReportEnabled,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
  flowId,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = useFeatureFlag(FeatureFlags.LinkReport);

  const effectiveFlowId = normalizeFlowFilter(flowId ?? filter.flowId);

  const dateRange = useMemo(() => {
    return performanceUtils.createDateRange(filter.dateFrom, filter.dateTo);
  }, [filter]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    performanceUtils.lineChartMargins.left + performanceUtils.lineChartMargins.right,
  );

  const lineChartQuery = useLinkLineChartQuery({ ...filter, linkId, flowId: effectiveFlowId }, chartPeriod);
  const engagementQuery = useLinkEngagementQuery({ ...filter, linkId, flowId: effectiveFlowId }, reportEnabled);

  const detailsQuery = useLinkDetailsQuery({ ...filter, linkId, flowId: effectiveFlowId }, reportEnabled);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: !!detailsQuery.data && detailsQuery.data.length > 0,
      tooltipContent: "No data available for export. This link contains no data.",
    });
  }, [detailsQuery.data, setHasExportData]);

  // Setting up correct export method
  useEffect(() => {
    setExportAction({
      method: linkExport,
      args: [{ ...filter, linkId, flowId: effectiveFlowId }, linkName],
      isExportEnabled: true,
    });
  }, [filter, linkExport, setExportAction, linkId, linkName, flowId, effectiveFlowId]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const lineData = useMemo(() => {
    const xData = [lineChartQuery.data?.date, lineChartQuery.data?.date];
    if (chartState === dailyActivity) {
      return {
        xData,
        yData: [lineChartQuery.data?.pageVisits, lineChartQuery.data?.clicks],
      };
    }
    return {
      xData,
      yData: [lineChartQuery.data?.cumulativePageVisits, lineChartQuery.data?.cumulativeClicks],
    };
  }, [chartState, lineChartQuery.data]);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  const linksReportColumnOptions: ColumnOption<LinkUserDetails>[] = [
    {
      name: "Name",
      width: 4,
      render(item) {
        const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
        const shouldShowLink =
          !isUserDeleted(item.Name) && (!includeAccounts || childAccountEnabled || !childAccountId);
        const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
          item.AccountId,
          accountId,
          childAccountId,
          hasCreateAccountsAddOn,
        );
        const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
          childAccountEnabled,
          isUserFromCurrentAccount,
          childAccountId,
          fallbackAccountId: item.AccountId,
        });
        const userName = <Tooltip target={<TextTruncate>{item.Name}</TextTruncate>} content={item.Name} />;
        return (
          <div>
            {shouldShowLink ? (
              <MoboLink
                accountId={linkAccountId}
                to={`/${RouteNames.peopleUsers}/${item.UserId}`}
                className="linkButton"
                target={linkTarget}
              >
                {userName}
              </MoboLink>
            ) : (
              userName
            )}
          </div>
        );
      },
    },
    {
      name: "Email",
      width: 5,
      render: ({ Email }: LinkUserDetails) => <Tooltip target={<TextTruncate>{Email}</TextTruncate>} content={Email} />,
    },
    {
      name: "Job Title",
      width: 3,
      render: ({ JobTitle }: LinkUserDetails) => (
        <Tooltip target={<TextTruncate>{JobTitle || "-"}</TextTruncate>} content={JobTitle || "-"} />
      ),
    },
    {
      name: "Department",
      width: 3,
      render: ({ Department }: LinkUserDetails) => (
        <Tooltip target={<TextTruncate>{Department || "-"}</TextTruncate>} content={Department || "-"} />
      ),
    },
    {
      name: "Status",
      width: 2,
      render: ({ Status }: LinkUserDetails) => {
        const icon = StatusToIcon[Status as keyof typeof StatusToIcon];
        return (
          <div className={styles.icon}>
            {icon && <FontAwesomeIcon icon={icon} className={styles[Status]} />}
            {Status}
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={lineChartTitles}
          showLegend
          legendLabels={activityLegendLabels}
          colorRange={activityColorScale}
          onChange={setChartState}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([
              lineChartQuery.data.clicks,
              lineChartQuery.data.pageVisits,
              lineChartQuery.data.cumulativeClicks,
              lineChartQuery.data.cumulativePageVisits,
            ]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xFormatterFunc={(d) => performanceUtils.getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
                colorRange={activityColorScale}
                {...lineData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>

      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={["User Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.barData.map((d: any) => d.value)) ? (
              <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementQuery.data.barData} />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>

      <div className={styles.performanceCardSection}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>

      <div className={styles.table}>
        <RequestStatusRenderer state={detailsQuery.status}>
          {detailsQuery.isSuccess && detailsQuery.data ? (
            <GenericPerformanceList
              title="People"
              rows={detailsQuery.data}
              columns={linksReportColumnOptions}
              filter={filter}
            />
          ) : null}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
  linkExport: bindActionCreators(doLinkExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(LinksReport);
export default ConnectedComponent;
